import React, { useState, useEffect } from "react";
import { Parallax } from "react-parallax";
import Footer from "./Footer";
import Header from "./Header";
import image2 from "../Assets/page_bg.jpg";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Logo from "../Assets/logo.png";
import "./Thanks.css";
import { FaCheckCircle, FaPrint, FaHome, FaShoppingCart, FaSpinner, FaPhone, FaWhatsapp } from "react-icons/fa";
import MobileNavigation from "./MobileNavigation";

function Thanks() {
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const order_id = params.get("order_id");

  const [orderData, setOrderData] = useState(null);
  const [settingsData, setSettingsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_ADMIN_API_BASE_URL;

  useEffect(() => {
    if (!order_id) {
      setError("No order ID provided in the URL.");
      setLoading(false);
      return;
    }

    const fetchSettingsDetails = async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}setting_detail.php`);
        if (response.data && response.data.head && response.data.head.code === 200) {
          setSettingsData(response.data.body);
        }
      } catch (error) {
        console.error("Error fetching settings details:", error);
      }
    };

    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.post(
          `${API_BASE_URL}order_details.php`,
          { order_id: String(order_id) },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data && response.data.head && response.data.head.code === 200) {
          setOrderData(response.data.body);
        } else {
          setError("Failed to fetch order details.");
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
        setError("Failed to load order details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
    fetchSettingsDetails();
  }, [order_id, API_BASE_URL]);

  // Open invoice in new tab for printing
  const handlePrint = () => {
    const invoiceWindow = window.open(
      `/invoice?order_id=${order_id}`,
      '_blank',
      'noopener,noreferrer'
    );
    if (invoiceWindow) {
      invoiceWindow.focus();
    }
  };

  // Loading Screen
  if (loading) {
    return (
      <div className="print-loading-container">
        <div className="print-loading-spinner">
          <FaSpinner className="print-spin-icon" />
        </div>
        <div className="print-loading-text">Loading your order details...</div>
        <p className="print-loading-subtext">Please wait while we prepare your invoice</p>
        <p className="print-order-id">
          Order ID: <strong>{order_id}</strong>
        </p>
      </div>
    );
  }

  // Error Screen
  if (error) {
    return (
      <div className="print-error-container">
        <div className="print-error-icon">⚠️</div>
        <h3 className="print-error-title">Unable to Load Order Details</h3>
        <p className="print-error-message">{error}</p>
        {order_id && (
          <div className="print-error-info">
            <p className="print-error-order">
              Your order was submitted with ID: <strong>{order_id}</strong>
            </p>
            <p className="print-error-save">
              Please save this order ID for future reference.
            </p>
          </div>
        )}
        <p className="print-error-contact">
          Our team will contact you soon regarding your order.
        </p>
        <div className="print-error-actions">
          <button
            className="print-btn-primary"
            onClick={() => navigate("/")}
          >
            <FaHome className="print-btn-icon" />
            Back to Home
          </button>
          <button
            className="print-btn-outline"
            onClick={() => navigate("/products")}
          >
            <FaShoppingCart className="print-btn-icon" />
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return null;
  }

  // Calculate values
  const subtotal = parseFloat(orderData.subtotal) || 0;
  const discountPercentage = parseFloat(orderData.discount_percentage) || 0;
  const discountAmount = parseFloat(orderData.discount_amt) || 0;
  const orderTotal =  parseFloat(orderData.subtotal) || 0;
  const packingPercentage = parseFloat(orderData.packing_percentage) || 0;
  const packingAmount = parseFloat(orderData.packing_amt) || 0;
  const couponDiscountAmount = parseFloat(orderData.coupon_discount) || 0;
  const hasCouponDiscount = couponDiscountAmount > 0;
  const grandTotalRaw = parseFloat(orderData.total) || 0;
  const grandTotal = Math.round(grandTotalRaw);

  return (
    <div className="print-thanks-page">
      <Header />

      {/* Parallax Header */}
      <Parallax
        bgImage={image2}
        bgImageAlt="background"
        strength={300}
        className="print-parallax-header"
        style={{ minHeight: "100px" }}
      >
        <div style={{ height: "230px" }}>
          <h1 className="print-page-title">
            Thank You!
          </h1>
          <p className="print-breadcrumb">
            <Link to="/" className="print-breadcrumb-link">Home</Link>
            <i className="bi bi-chevron-right print-breadcrumb-icon"></i>
            Order Confirmation
          </p>
        </div>
      </Parallax>

      <div className="print-container">
        {/* Success Message */}
        <div className="print-success-alert">
          <div className="print-success-content">
            <FaCheckCircle className="print-success-icon" />
            <div className="print-success-text">
              <h4 className="print-success-title">Order Placed Successfully!</h4>
              <p className="print-success-message">
                Thank you for your enquiry. We will contact you soon. Your order ID is:
                <strong className="print-success-orderid">{orderData.order_id}</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="print-action-bar">
          <button onClick={handlePrint} className="print-action-btn">
            <FaPrint className="print-action-icon" /> View Invoice / Print
          </button>

          <Link to="/" className="print-action-link">
            <FaHome className="print-action-icon" />
            Back to Home
          </Link>
          
          <a 
            href={`https://wa.me/${settingsData?.whatsapp || settingsData?.phone || ''}?text=Hello,%20I%20have%20placed%20an%20order%20with%20ID:%20${orderData.order_id}`}
            target="_blank" 
            rel="noopener noreferrer"
            className="print-action-whatsapp"
          >
            <FaWhatsapp className="print-action-icon" />
            Contact on WhatsApp
          </a>
        </div>

        {/* Invoice Display */}
        <div className="print-invoice-wrapper">
          <div className="print-watermark">BK Crackers</div>
          <div className="print-invoice-container">
            <table className="print-invoice-table">
              <tbody>
                {/* Header with Logo */}
                <tr>
                  <td className="print-invoice-header">
                    <img
                      src={Logo}
                      alt="Mathi Crackers Logo"
                      className="print-company-logo"
                      onLoad={() => setImageLoaded(true)}
                    />
                  </td>
                </tr>

                {/* Order Info */}
                <tr>
                  <td className="print-order-info">
                    <div className="print-order-row">
                      <div>
                        <strong>Order ID:</strong> {orderData.order_id}
                      </div>
                      <div>
                        <strong>Date:</strong> {orderData.orderdate}
                      </div>
                    </div>
                  </td>
                </tr>

                {/* Billing Information */}
                <tr>
                  <td className="print-billing-section">
                    <div className="print-billing-card">
                      <h3 className="print-billing-title">Billing Information</h3>
                      <p className="print-billing-name">{orderData.billing_name}</p>
                      <p className="print-billing-address">
                        {orderData.billing_address}, {orderData.billing_city},{" "}
                        {orderData.billing_state}, {orderData.billing_pincode}
                      </p>
                      <p className="print-billing-mobile">
                        <strong>Mobile:</strong> {orderData.mobile}
                      </p>
                      <p className="print-billing-email">
                        <strong>Email:</strong> {orderData.email}
                      </p>
                      {orderData.coupen_code && (
                        <p className="print-billing-coupon">
                          <strong>Referral ID:</strong> {orderData.coupen_code}
                        </p>
                      )}
                    </div>
                  </td>
                </tr>

                {/* Product Table - Updated: Price column shows unit price, No discount column, Total = qty × price */}
                <tr>
                  <td className="print-products-section">
                    <table className="print-product-grid">
                      <thead>
                        <tr>
                          <th className="print-col-sno">S.No</th>
                          <th className="print-col-product">Product Name</th>
                          <th className="print-col-qty">Qty</th>
                          <th className="print-col-price text-right">Price (₹)</th>
                          <th className="print-col-total text-right">Total (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderData.order_details && orderData.order_details.map((item, index) => {
                          // Unit price (price per 1 qty)
                          const unitPrice = parseFloat(item.amount) || 0;
                          const qty = parseFloat(item.qty) || 0;
                          // Total = qty × unit price
                          const totalAmount = unitPrice * qty;
                          
                          return (
                            <tr key={index} className="print-product-row">
                              <td className="print-cell-sno">{index + 1}</td>
                              <td className="print-cell-product">{item.product_name}</td>
                              <td className="print-cell-qty">{qty}</td>
                              <td className="print-cell-price text-right">₹ {unitPrice.toFixed(2)}</td>
                              <td className="print-cell-total text-right"><b>₹ {totalAmount.toFixed(2)}</b></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </td>
                </tr>

                {/* Totals Section - No changes here */}
                <tr>
                  <td className="print-totals-section">
                    <table className="print-totals-table">
                      <tbody>
                        {/* Subtotal */}
                        {/* <tr>
                          <td align="right" style={{ padding: "8px", borderTop: "1px dashed #ddd" }}>Subtotal:</td>
                          <td align="right" style={{ padding: "8px", borderTop: "1px dashed #ddd" }}>₹ {subtotal.toFixed(2)}</td>
                        </tr> */}
                        
                        {/* Discount Total - Only show if > 0 */}
                        {/* {discountAmount > 0 && (
                          <tr>
                            <td align="right" style={{ fontWeight: "bold", padding: "8px", color: "#dc3545" }}>Discount ({discountPercentage}%):</td>
                            <td align="right" style={{ padding: "8px", color: "#dc3545" }}>- ₹ {discountAmount.toFixed(2)}</td>
                          </tr>
                        )} */}

                        {/* Order Total after discount */}
                        <tr>
                          <td align="right" style={{ fontWeight: "bold", borderTop: "1px dashed #ddd", padding: "8px" }}>Order Total:</td>
                          <td align="right" style={{ borderTop: "1px dashed #ddd", padding: "8px" }}>₹ {orderTotal.toFixed(2)}</td>
                        </tr>

                        {/* Packing Charge - Only show if > 0 */}
                        {packingAmount > 0 && (
                          <tr>
                            <td align="right" style={{ fontWeight: "bold", padding: "8px" }}>Packing Charge ({packingPercentage}%):</td>
                            <td align="right" style={{ padding: "8px" }}>+ ₹ {packingAmount.toFixed(2)}</td>
                          </tr>
                        )}

                        {/* Coupon Discount - Only show if applied */}
                        {hasCouponDiscount && (
                          <tr>
                            <td align="right" style={{ fontWeight: "bold", padding: "8px", color: "#28a745" }}>Coupon Discount:</td>
                            <td align="right" style={{ padding: "8px", color: "#28a745" }}>- ₹ {couponDiscountAmount.toFixed(2)}</td>
                          </tr>
                        )}

                        {/* Grand Total */}
                        <tr style={{ borderTop: "2px solid #333", fontWeight: "bold" }}>
                          <td align="right" style={{ fontSize: "16px", padding: "10px 8px" }}>Grand Total:</td>
                          <td align="right" style={{ fontSize: "16px", color: "#28a745", padding: "10px 8px" }}>₹ {grandTotal.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>

                {/* Footer Message */}
                <tr>
                  <td className="print-footer-message">
                    <p className="print-diwali-wish">Thank you for shopping with us!</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="print-action-bar mt-3">
          <div className="print-confirm-btn">
             Click Sumbit on Whtasapp to Complete Your Order
          </div>
        </div>

        {/* Action Buttons */}
        <div className="print-action-bar mt-3">
          <button onClick={handlePrint} className="print-action-btn">
            <FaPrint className="print-action-icon" /> View Invoice / Print
          </button>

          <Link to="/" className="print-action-link">
            <FaHome className="print-action-icon" />
            Back to Home
          </Link>

          <a
            href={`https://wa.me/${settingsData?.whatsapp || settingsData?.phone || ''}?text=${encodeURIComponent(
              `Hello,
          
                                I have placed an order with the following details:
          
                                Order ID: ${orderData.order_id}
                                Date: ${orderData.orderdate}
          
                                Customer Details:
                                Name: ${orderData.billing_name}
                                Mobile: ${orderData.mobile}
                                Email: ${orderData.email}
                                Address: ${orderData.billing_address}, ${orderData.billing_city}, ${orderData.billing_state}, ${orderData.billing_pincode}
                                Order Total: ₹${orderData.total || 0}
          
                                ${orderData.coupen_code ? `Referral ID: ${orderData.coupen_code}\n` : ''}
          
                                Please confirm my order. Thank you!`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="print-action-whatsapp"
          >
            <FaWhatsapp className="print-action-icon" />
            Submit on WhatsApp
          </a>
        </div>
      </div>

      <Footer />
      <MobileNavigation />
    </div>
  );
}

export default Thanks;